import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { PaymentPro } from "@/api/events/models/EventInput";
import { getCurrentClientData } from "@/services/authServices";
import { EventsService } from "@/api/events";
import { useToast } from "@/components/ui/use-toast";

export interface PlanFeature {
  name: string;
  included: boolean;
  highlight?: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number | "Free";
  billingPeriod: "monthly" | "yearly" | "one-time";
  features: PlanFeature[];
  popular?: boolean;
  buttonText: string;
  color?: string;
}

export interface SubscriptionProps {
  account_type: string;
  status: string;
  valid_until: null;
}
interface SubscriptionPlansProps {
  plans: SubscriptionPlan[];
  currentPlanId?: string;
  onSelectPlan: (planId: SubscriptionPlan) => void;
  setPaymentSecret: (value: React.SetStateAction<string>) => void;
  setPaymentScreen: React.Dispatch<React.SetStateAction<boolean>>;
  accountType?: {
    account_type: string;
    status: string;
    valid_until: null | string;
  };
}

export const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  plans,
  currentPlanId,
  onSelectPlan,
  setPaymentSecret,
  setPaymentScreen,
  accountType,
}) => {
  const navigate = useNavigate();
  const loggedInUser = getCurrentClientData();
  const { registerPro } = EventsService;
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  const handleUpgradeClick = async (planId: SubscriptionPlan) => {
    // If it's the current plan, navigate to billing page
    // if (planId.id === "business") return;
    if (planId.id === currentPlanId) {
      navigate("/billing");
      return;
    }

    setLoading(true);
    // Otherwise trigger the upgrade flow
    const payLoad: PaymentPro = {
      account_type: planId.id,
      subscription_type: planId.billingPeriod,
      subscription_price: planId.price.toString(),
      months_valid: "1",
      payment_method_id: "pm_card_visa",
    };
    try {
      const res: any = await registerPro(loggedInUser.token, payLoad);
      if (res.statusCode === 200) {
        setPaymentScreen(true);
        setLoading(false);
        // setTransactions([newTransaction, ...transactions]);
        onSelectPlan(planId);
        console.log("Payment response:", res);

        setPaymentSecret(res?.body?.client_secret);

        // toast({
        //   title: "Subscription Updated",
        //   description: `You are now on the ${plan.name} plan.`,
        // });
      } else {
        toast({
          title: "Subscription Failed",
          description: res.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Subscription Failed",
        description: "There was an error updating your subscription.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
    // onSelectPlan(planId);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {plans.map((plan) => (
        <Card
          key={plan.id}
          className={`relative overflow-hidden ${
            plan.popular ? "border-cecure-700 shadow-lg" : ""
          }`}
        >
          {plan.popular && (
            <div className="absolute top-0 right-0 bg-cecure-700 text-white px-3 py-1 text-xs font-semibold">
              Most Popular
            </div>
          )}
          <CardHeader>
            <CardTitle>{plan.name}</CardTitle>
            <CardDescription>{plan.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <span className="text-3xl font-bold">
                {typeof plan.price === "number" ? `$${plan.price}` : plan.price}
              </span>
              {typeof plan.price === "number" && (
                <span className="text-muted-foreground ml-1">
                  /{plan.billingPeriod}
                </span>
              )}
            </div>

            <ul className="space-y-2">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  {feature.included ? (
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-gray-300 mr-2 flex-shrink-0" />
                  )}
                  <span className={feature.highlight ? "font-medium" : ""}>
                    {feature.name}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter>
            <Button
              disabled={
                loading ||
                (plan.id === accountType?.account_type &&
                  accountType?.status === "active")
              }
              onClick={() => {
                handleUpgradeClick(plan);
              }}
              className={`w-full ${
                plan.id !== accountType?.account_type
                  ? "bg-cecure-700 hover:bg-cecure-800"
                  : ""
              }`}
              variant={
                plan.id === accountType?.account_type ? "outline" : "default"
              }
            >
              {plan.id === accountType?.account_type &&
              accountType?.status === "inactive"
                ? "Reactivate Plan"
                : plan.id === accountType?.account_type &&
                  accountType?.status === "active"
                ? "Current Plan"
                : plan.buttonText}
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default SubscriptionPlans;
